import { useEffect, useRef, useState, useCallback } from 'react';
import { mountRootParcel } from 'single-spa';

/**
 * Hook para gestionar banners publicitarios usando shared-ui
 * Permite montar y desmontar banners de forma dinámica
 * 
 * @returns {Object} Objeto con referencias, estado y funciones de control
 */
const useBanners = () => {
  // Referencias para los contenedores DOM donde se montarán los banners
  const banner1Ref = useRef(null);
  const banner2Ref = useRef(null);
  
  // Estado para hacer tracking de los parcels montados
  const [parcels, setParcels] = useState({
    banner1: null,
    banner2: null
  });

  /**
   * Monta un banner específico en el DOM
   * @param {string} bannerKey - Identificador del banner ('banner1' o 'banner2')
   * @param {Object} adCardConfig - Configuración del AdCard
   * @param {string} adCardConfig.color - Color del banner ("primary", "secondary", "success", "info", "warning", "danger")
   * @param {string} adCardConfig.size - Tamaño del banner ("small", "medium", "big")
   * @param {boolean} adCardConfig.shape - Si el banner tiene forma especial
   * @param {boolean} adCardConfig.paddingFixed - Si usa padding fijo (p-4)
   * @param {boolean} adCardConfig.fullWidth - Si ocupa todo el ancho disponible
   * @param {string} adCardConfig.customClass - Clases CSS adicionales
   * @param {string|React.ReactNode} adCardConfig.children - Contenido del banner
   */
  const mountBanner = useCallback(async (bannerKey, adCardConfig = {}) => {
    // Mapa de referencias
    const refs = { banner1: banner1Ref, banner2: banner2Ref };
    const ref = refs[bannerKey];
    
    // Verificar que existe el elemento DOM
    if (!ref?.current) {
      console.warn(`[useBanners] DOM element not found for banner: ${bannerKey}`);
      return;
    }

    try {
      // Importar dinámicamente el parcel de shared-ui
      const parcelConfig = await System.import("@a365/shared-ui");
      
      // Montar el parcel con la configuración específica
      const parcel = mountRootParcel(parcelConfig, {
        domElement: ref.current,
        show: "ad-card", // Indica que queremos mostrar AdCardShared
        adCardConfig: adCardConfig // Pasar toda la configuración como objeto
      });

      // Actualizar el estado con la referencia del parcel
      setParcels(prev => ({ ...prev, [bannerKey]: parcel }));
      
      console.log(`[useBanners] Banner ${bannerKey} mounted successfully`);
      
    } catch (error) {
      console.error(`[useBanners] Error mounting banner ${bannerKey}:`, error);
    }
  }, []); // Sin dependencias para evitar cambios

  /**
   * Desmonta un banner específico
   * @param {string} bannerKey - Identificador del banner a desmontar
   */
  const unmountBanner = useCallback(async (bannerKey) => {
    const parcel = parcels[bannerKey];
    
    if (parcel) {
      try {
        await parcel.unmount();
        setParcels(prev => ({ ...prev, [bannerKey]: null }));
        console.log(`[useBanners] Banner ${bannerKey} unmounted successfully`);
      } catch (error) {
        console.error(`[useBanners] Error unmounting banner ${bannerKey}:`, error);
      }
    }
  }, [parcels]);

  /**
   * Monta múltiples banners con sus respectivas configuraciones
   * @param {Object} banner1Config - Configuración para banner1
   * @param {Object} banner2Config - Configuración para banner2
   */
  const mountAllBanners = (banner1Config = {}, banner2Config = {}) => {
    // Pequeño delay para asegurar que los elementos DOM estén disponibles
    setTimeout(() => {
      if (Object.keys(banner1Config).length > 0) {
        mountBanner('banner1', banner1Config);
      }
      if (Object.keys(banner2Config).length > 0) {
        mountBanner('banner2', banner2Config);
      }
    }, 100);
  };

  /**
   * Desmonta todos los banners activos
   */
  const unmountAllBanners = async () => {
    const promises = Object.keys(parcels)
      .filter(key => parcels[key]) // Solo desmontar los que están montados
      .map(key => unmountBanner(key));
    
    await Promise.all(promises);
  };

  /**
   * Recarga un banner específico con nueva configuración
   * @param {string} bannerKey - Identificador del banner
   * @param {Object} newConfig - Nueva configuración
   */
  const reloadBanner = async (bannerKey, newConfig = {}) => {
    await unmountBanner(bannerKey);
    setTimeout(() => mountBanner(bannerKey, newConfig), 150);
  };

  // Cleanup automático cuando se desmonta el componente
  useEffect(() => {
    return () => {
      // Cleanup sin async para evitar problemas de lifecycle
      Object.keys(parcels).forEach(key => {
        if (parcels[key]) {
          parcels[key].unmount().catch(console.error);
        }
      });
    };
  }, []); // Solo ejecutar una vez al desmontar

  return {
    // Referencias para los contenedores DOM
    banner1Ref,
    banner2Ref,
    
    // Estado de los banners
    isBanner1Loaded: !!parcels.banner1,
    isBanner2Loaded: !!parcels.banner2,
    parcels,
    
    // Funciones principales
    mountBanner,
    unmountBanner,
    mountAllBanners,
    unmountAllBanners,
    reloadBanner
  };
};

export { useBanners };